import { buildMusicName } from '@any-listen/common/tools'

import { showSimpleConfirmModal } from '@/components/apis/dialog'
import { hideNotify, showNotify, updateNotify } from '@/components/apis/notify'
import { addInfo } from '@/modules/dislikeList/actions'
import { getListMusics, removeListMusics } from '@/modules/musicLibrary/actions'
import { updateListMusicsPosition } from '@/modules/musicLibrary/store/actions'
import { addPlayLaterMusic, playList, playOnlineList } from '@/modules/player/store/actions'
import type { OnlineListMetaInfo } from '@/modules/player/store/playerActions'
import { settingState } from '@/modules/setting/store/state'
import { i18n } from '@/plugins/i18n'
import { clipboardWriteText, openDirInExplorer } from '@/shared/ipc/app'
import { downloadMusic, getDownloadProgress } from '@/shared/ipc/music'

export const playLocalListMusic = async (listId: string, musicInfo: AnyListen.Music.MusicInfo, isClianHistory?: boolean) => {
  const list = await getListMusics(listId)
  const idx = list.findIndex((m) => m.id == musicInfo.id)
  if (idx < 0) return
  void playList(listId, list, idx, isClianHistory)
}

export const playOnlineListMusic = async (
  listId: string,
  list: AnyListen.Music.MusicInfo[],
  musicInfo: AnyListen.Music.MusicInfo,
  source: AnyListen.Player.SourceType,
  metaInfo: OnlineListMetaInfo,
  isClianHistory?: boolean
) => {
  const idx = list.findIndex((m) => m.id == musicInfo.id)
  if (idx < 0) return
  void playOnlineList(listId, list, idx, source, metaInfo, isClianHistory)
}

export const playMusic = async (
  listId: string,
  list: AnyListen.Music.MusicInfo[],
  musicInfo: AnyListen.Music.MusicInfo,
  source: AnyListen.Player.SourceType,
  metaInfo?: OnlineListMetaInfo,
  isClianHistory?: boolean
) => {
  if (source === 'local') {
    await playLocalListMusic(listId, musicInfo, isClianHistory)
  } else {
    if (!metaInfo) throw new Error('miss metaInfo for online music')
    await playOnlineListMusic(listId, list, musicInfo, source, metaInfo, isClianHistory)
  }
}

let clickTime = 0
let clickInfo: AnyListen.Music.MusicInfo | null = null
export const musicClick = async (
  list: AnyListen.Music.MusicInfo[],
  listId: string,
  musicInfo: AnyListen.Music.MusicInfo,
  source: AnyListen.Player.SourceType,
  metaInfo?: OnlineListMetaInfo
) => {
  if (window.performance.now() - clickTime > 400 || clickInfo !== musicInfo) {
    clickTime = window.performance.now()
    clickInfo = musicInfo
    return
  }
  clickTime = 0
  clickInfo = null

  void playMusic(listId, list, musicInfo, source, metaInfo)
}

export const playMusicLater = async (
  listId: string,
  musicInfo: AnyListen.Music.MusicInfo,
  selectedList: AnyListen.Music.MusicInfo[],
  source: AnyListen.Player.SourceType,
  removeAllSelect?: () => void
) => {
  if (selectedList.length) {
    await addPlayLaterMusic(selectedList, listId, source)
    removeAllSelect?.()
  } else {
    await addPlayLaterMusic([musicInfo], listId, source)
  }
}

export const updateMusicPosition = async (listId: string, position: number, ids: string[]) => {
  const list = await getListMusics(listId)
  position = Math.min(position, list.length)
  await updateListMusicsPosition({
    listId,
    position: position - 1,
    ids,
  })
}

export const copyName = (musicInfo: AnyListen.Music.MusicInfo) => {
  void clipboardWriteText(buildMusicName(settingState.setting['download.fileName'], musicInfo.name, musicInfo.singer))
}

export const locateMusic = (musicInfo: AnyListen.Music.MusicInfoLocal) => {
  void openDirInExplorer(musicInfo.meta.filePath)
}

export const dislikeMusic = async (musicInfo: AnyListen.Music.MusicInfo) => {
  const confirm = await showSimpleConfirmModal(
    musicInfo.singer
      ? i18n.t('music_list__dislike_music_singer_tip', { name: musicInfo.name, singer: musicInfo.singer })
      : i18n.t('music_list__dislike_music_tip', { name: musicInfo.name }),
    {
      cancelBtn: i18n.t('cancel_button_text_2'),
      confirmBtn: i18n.t('confirm_button_text'),
    }
  )
  if (!confirm) return
  await addInfo([{ name: musicInfo.name, singer: musicInfo.singer }])
}

export const removeMusic = async (
  listId: string,
  musicInfo: AnyListen.Music.MusicInfo,
  selectedList: AnyListen.Music.MusicInfo[],
  removeAllSelect?: () => void
) => {
  if (selectedList.length) {
    const confirm =
      selectedList.length > 1
        ? await showSimpleConfirmModal(i18n.t('music_list__remove_music_tip', { len: selectedList.length }), {
            confirmBtn: i18n.t('confirm_button_text_2'),
          })
        : true
    if (!confirm) return
    await removeListMusics(
      listId,
      selectedList.map((m) => m.id)
    )
    removeAllSelect?.()
  } else {
    await removeListMusics(listId, [musicInfo.id])
  }
}

const formatDownloadSize = (size: number): string => {
  if (size < 1024) return `${size}B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)}KB`
  return `${(size / 1024 / 1024).toFixed(1)}MB`
}

// 正在下载的歌曲 id，避免重复下载
const downloadingTasks = new Set<string>()

/**
 * 下载在线歌曲到服务器
 * 仅在 web 模式下可用，下载完成后歌曲会自动加入默认列表
 */
export const downloadMusicToServer = async (musicInfo: AnyListen.Music.MusicInfo) => {
  if (musicInfo.isLocal || downloadingTasks.has(musicInfo.id)) return
  downloadingTasks.add(musicInfo.id)
  const name = musicInfo.singer ? `${musicInfo.name} - ${musicInfo.singer}` : musicInfo.name
  const notifyId = showNotify(`开始下载到服务器：${name}`, 60)

  // 轮询下载进度并更新提示
  let stopped = false
  const pollProgress = async () => {
    while (!stopped) {
      await new Promise((resolve) => setTimeout(resolve, 800))
      if (stopped) break
      const progress = await getDownloadProgress(musicInfo.id).catch(() => null)
      if (!progress) continue
      const progressText =
        progress.total != null
          ? `${Math.floor((progress.downloaded / progress.total) * 100)}%`
          : formatDownloadSize(progress.downloaded)
      updateNotify(notifyId, `正在下载到服务器：${name} ${progressText}`)
    }
  }
  void pollProgress()

  try {
    await downloadMusic({ musicInfo })
    hideNotify(notifyId)
    showNotify(`下载完成，已加入默认列表：${name}`, 5)
  } catch (err) {
    hideNotify(notifyId)
    showNotify(`下载失败：${name}（${(err as Error).message}）`, 8)
  } finally {
    stopped = true
    downloadingTasks.delete(musicInfo.id)
  }
}
